
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Upload } from "lucide-react";
import { uploadImage } from "@/utils/imageUpload";
import { useToast } from "@/hooks/use-toast";
import { validateImageFile, createImagePreview } from "@/utils/image/dialogUtils";
import ImageFileSelector from "@/components/admin/dialog/ImageFileSelector";
import ImageUploadPreview from "@/components/admin/dialog/ImageUploadPreview";
import ImageMetadataFields from "@/components/admin/dialog/ImageMetadataFields";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

interface ImageUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImageUploaded: (imageUrl: string, altText: string, titleText: string) => void;
}

const ImageUploadDialog: React.FC<ImageUploadDialogProps> = ({ 
  open, 
  onOpenChange,
  onImageUploaded
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [altText, setAltText] = useState("");
  const [titleText, setTitleText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const { toast } = useToast();

  // Reset the form when the dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setAltText("");
      setTitleText("");
      setPreviewUrl(null);
    }
  }, [open]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate the file
      const validationError = validateImageFile(file, MAX_FILE_SIZE);
      if (validationError) {
        toast({
          title: "Invalid file",
          description: validationError,
          variant: "destructive",
        });
        return;
      }
      
      setSelectedFile(file);
      
      try {
        // Create preview
        const preview = await createImagePreview(file);
        setPreviewUrl(preview);
      } catch (error) {
        console.error("Error creating preview:", error);
        toast({
          title: "Preview error",
          description: "Failed to create image preview",
          variant: "destructive",
        });
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No image selected",
        description: "Please select an image to upload",
        variant: "destructive",
      });
      return;
    }

    if (!altText.trim()) {
      toast({
        title: "Alt text required",
        description: "Please enter alt text for accessibility and SEO",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsUploading(true);
      
      // Upload the image to the blog_images bucket
      const imageUrl = await uploadImage(selectedFile, "blog_images/inline");
      
      // Call the callback with the uploaded image URL and metadata
      onImageUploaded(imageUrl, altText, titleText);
      
      // Reset the form
      setSelectedFile(null);
      setAltText("");
      setTitleText("");
      setPreviewUrl(null);
      
      // Close the dialog
      onOpenChange(false);
      
      toast({
        title: "Image uploaded",
        description: "The image has been uploaded and inserted into your content",
      });
    } catch (error: any) {
      console.error("Error uploading image:", error);
      toast({
        title: "Upload failed",
        description: error.message || "There was a problem uploading your image",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-background border-2 border-border shadow-lg">
        <DialogHeader className="border-b border-border pb-4">
          <DialogTitle className="text-xl font-bold text-foreground">Insert Image</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-5 py-4">
          <ImageFileSelector 
            handleFileChange={handleFileChange}
            selectedFile={selectedFile}
            isUploading={isUploading}
          />
          
          <ImageUploadPreview previewUrl={previewUrl} />
          
          <ImageMetadataFields
            altText={altText}
            setAltText={setAltText}
            titleText={titleText}
            setTitleText={setTitleText}
            isUploading={isUploading}
          />
        </div>
        
        <DialogFooter className="border-t border-border pt-4 gap-3">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)} 
            disabled={isUploading}
            className="border-2"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            disabled={isUploading}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                Insert Image
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageUploadDialog;
